import cx from 'classnames';
import { Icon } from '@components/icon/Icon';
import { appendGuestCountLabel } from '@shared/utils/guestCount';
import { ISOTimeTo12HourTime } from '@utils/time';
import typography from '~styles/typography.scss';
import type { LightspeedOrder } from '@shared/types/reservations';
import { type OperaHotelBlock, type Server } from '../apiHelpers';
import styles from './ActiveReservationCard.scss';
import { OccupantSpend } from './OccupantSpend';
import { ServerBadge } from './servers/ServerBadge';

interface OccupantCardContentProps extends React.HTMLProps<HTMLDivElement> {
  guestCount: number;
  guestName: string;
  tableName: string;
  floorPlanName: string;
  server: Server | null;
  time: string;
  hasDietaryRestrictionOrAllergy?: boolean;
  hasGuestNote?: boolean;
  hasNoteToRestaurant?: boolean;
  hotel?: OperaHotelBlock | null;
  isHostBooked?: boolean;
  isVIP?: boolean;
  /** Left out for occupants that have no Lightspeed order in the response. */
  lightspeedOrder?: LightspeedOrder | null;
}

export const OccupantCardContent = ({
  guestCount,
  guestName,
  tableName,
  floorPlanName,
  server,
  time,
  hasDietaryRestrictionOrAllergy = false,
  hasGuestNote = false,
  hasNoteToRestaurant = false,
  hotel = null,
  isHostBooked = false,
  isVIP = false,
  lightspeedOrder,
}: OccupantCardContentProps) => (
  <div className={styles.infoContainer}>
    <div className={styles.timeAndStatusRow}>
      <div className={cx(typography.h8, styles.time)}>
        {ISOTimeTo12HourTime(time)}
        {isVIP && (
          <Icon
            ariaLabel="Is a VIP"
            className={cx(styles.vipIcon)}
            name="crown"
            role="img"
            fill="var(--gold)"
          />
        )}
        {hotel?.isInHouse && (
          <>
            <Icon
              ariaLabel="In house hotel guest"
              className={cx(styles.strokeIcon, styles.hotelIcon)}
              name="key"
              role="img"
            />
            {hotel.roomNumber && (
              <span className={styles.roomNumber}>{hotel.roomNumber}</span>
            )}
          </>
        )}
        {hotel && !hotel.isInHouse && (
          <Icon
            ariaLabel="Hotel guest"
            className={styles.strokeIcon}
            name="key"
            role="img"
          />
        )}
        {isHostBooked && (
          <Icon
            ariaLabel="Host booked"
            className={styles.fillIcon}
            name="roomService"
            role="img"
          />
        )}
        {hasGuestNote && (
          <Icon
            ariaLabel="Has guest notes"
            className={cx(styles.strokeIcon, styles.hasNoteIcon)}
            name="note"
            role="img"
          />
        )}
        {hasDietaryRestrictionOrAllergy && (
          <Icon
            ariaLabel="Has dietary restriction or allergy"
            className={styles.fillIcon}
            name="cutlery"
            role="img"
          />
        )}
        {hasNoteToRestaurant && (
          <Icon
            ariaLabel="Has note from guest"
            className={styles.fillIcon}
            name="messageCircle"
            role="img"
          />
        )}
      </div>
      <div className={styles.row}>
        <div className={cx(typography.t3, styles.tableName)}>{tableName}</div>
        {server && (
          <ServerBadge
            serverName={server.name}
            badgeColor={server.hexColor}
            variant="small"
          />
        )}
      </div>
    </div>
    <div className={styles.nameAndGuestCountRow}>
      <div className={styles.userFullName}>{guestName}</div>
      <div className={styles.guestStack}>
        <div
          aria-label={appendGuestCountLabel(guestCount)}
          className={styles.guestCount}
        >
          <Icon name="couple" /> {guestCount}
        </div>
      </div>
    </div>
    {lightspeedOrder !== undefined && <OccupantSpend order={lightspeedOrder} />}
    <div className={cx(typography.t3, styles.row, styles.floorPlanName)}>
      {floorPlanName}
    </div>
  </div>
);
